import React, { useState, useEffect } from 'react';
import { List, Avatar, Typography, Button, Badge, Spin, Collapse, Tooltip, Tag } from 'antd';
import { 
  UserOutlined, 
  MenuFoldOutlined, 
  TeamOutlined, 
  CompassOutlined, 
  ReloadOutlined,
  WifiOutlined,
  DisconnectOutlined,
  ClockCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Member } from '../../types/chat';
import { useWebSocket, OnlineMembersData, RoomOnlineMembersData, OnlineUser } from '../../contexts/WebSocketProvider';
import '../styles/MembersList.css';

const { Text, Title } = Typography;
const { Panel } = Collapse;

interface MembersListProps {
  members: Member[];
  currentRoomId: string | null;
  toggleMembersSidebar: () => void;
  onMentionUser?: (user: OnlineUser) => void;
}

const MembersList: React.FC<MembersListProps> = ({ members, currentRoomId, toggleMembersSidebar, onMentionUser }) => {
  const { fetchOnlineMembers, onlineMembers } = useWebSocket();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Состояние для активных панелей с загрузкой из localStorage
  const [activePanels, setActivePanels] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('membersList_activePanels');
      return saved ? JSON.parse(saved) : ['internal', 'matrix'];
    } catch (error) {
      console.warn('Error loading active panels from localStorage:', error);
      return ['internal', 'matrix'];
    }
  });

  // Сохранение состояния панелей в localStorage
  const handlePanelChange = (activeKeys: string | string[]) => {
    const keys = Array.isArray(activeKeys) ? activeKeys : [activeKeys].filter(Boolean);
    setActivePanels(keys);
    
    try {
      localStorage.setItem('membersList_activePanels', JSON.stringify(keys));
    } catch (error) {
      console.warn('Error saving active panels to localStorage:', error);
    }
  };

  // Функция для получения данных текущей комнаты
  const getCurrentRoomData = (): RoomOnlineMembersData | null => {
    if (!onlineMembers || !currentRoomId) {
      console.log(`📊 MembersList: getCurrentRoomData() - onlineMembers: ${!!onlineMembers}, currentRoomId: ${currentRoomId}`);
      return null;
    }
    
    const roomData = onlineMembers.rooms[currentRoomId] || null;
    console.log(`📊 MembersList: getCurrentRoomData() для комнаты ${currentRoomId}:`, {
      found: !!roomData,
      availableRooms: Object.keys(onlineMembers.rooms),
      roomDataSummary: roomData?.summary
    });
    
    return roomData;
  };

  // Загружаем онлайн участников при монтировании компонента (только если данных нет)
  useEffect(() => {
    // Если данных еще нет, делаем первоначальную загрузку
    if (!onlineMembers) {
      loadOnlineMembers();
    }
    
    // Убираем автоматическое обновление - теперь данные приходят через WebSocket
    // const interval = setInterval(loadOnlineMembers, 30000);
    // return () => clearInterval(interval);
  }, [onlineMembers]);

  const loadOnlineMembers = async () => {
    setLoading(true);
    try {
      await fetchOnlineMembers();
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading online members:', error);
    } finally {
      setLoading(false);
    }
  };

  // Отслеживаем обновления онлайн участников через WebSocket
  useEffect(() => {
    if (onlineMembers) {
      setLastUpdated(new Date());
    }
  }, [onlineMembers]);

  // Загружаем данные при смене комнаты
  useEffect(() => {
    if (currentRoomId && !getCurrentRoomData()) {
      console.log(`🔄 Загружаем участников для новой комнаты: ${currentRoomId}`);
      loadOnlineMembers();
    }
  }, [currentRoomId, onlineMembers]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <WifiOutlined style={{ color: '#52c41a' }} />;
      case 'offline':
        return <DisconnectOutlined style={{ color: '#8c8c8c' }} />;
      case 'unavailable':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return '#52c41a';
      case 'offline':
        return '#8c8c8c';
      case 'unavailable':
        return '#faad14';
      default:
        return '#8c8c8c';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online':
        return t('members.online');
      case 'offline':
        return t('members.offline');
      case 'unavailable':
        return t('members.unavailable');
      default:
        return t('members.unknown');
    }
  };

  const formatLastSeen = (lastSeen: number | null | undefined) => {
    if (!lastSeen) return '';
    
    const now = Date.now();
    const diff = now - lastSeen;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return t('members.justNow');
    if (minutes < 60) return `${minutes} ${t('members.minutes')}`;
    if (hours < 24) return `${hours} ${t('members.hours')}`;
    return `${days} ${t('members.days')}`;
  };

  // Функция для получения приоритета статуса при сортировке
  const getStatusPriority = (status: string): number => {
    switch (status) {
      case 'online':
        return 1; // Самый высокий приоритет
      case 'unavailable':
        return 2; // Второй приоритет
      case 'offline':
        return 3; // Третий приоритет
      case 'unknown':
        return 4; // Самый низкий приоритет
      default:
        return 5; // Для неизвестных статусов
    }
  };

  // Функция для сортировки участников по статусу
  const sortUsersByStatus = (users: OnlineUser[]): OnlineUser[] => {
    return [...users].sort((a, b) => {
      const priorityA = getStatusPriority(a.status);
      const priorityB = getStatusPriority(b.status);
      
      // Если приоритеты одинаковые, сортируем по имени
      if (priorityA === priorityB) {
        const nameA = a.display_name?.toLowerCase() || '';
        const nameB = b.display_name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      }
      
      return priorityA - priorityB;
    });
  };

  const renderUser = (user: OnlineUser, isInternal: boolean = false) => {
    // Обработчик клика для упоминания пользователей Matrix (поддержки)
    const handleUserClick = () => {
      if (!isInternal && onMentionUser && user.user_id) {
        onMentionUser(user);
      }
    };

    return (
      <List.Item 
        key={`${isInternal ? 'internal' : 'matrix'}-${user.username || user.user_id}`} 
        className="member-item"
        onClick={handleUserClick}
        style={{
          cursor: !isInternal && onMentionUser ? 'pointer' : 'default'
        }}
      >
          <div className="member-info" style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                <Avatar 
                  style={{ 
                    backgroundColor: isInternal ? '#1890ff' : '#f56a00', 
                    marginRight: 12 
                  }}
                  icon={<UserOutlined />}
                  size="small"
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text 
                    className="member-name" 
                    style={{ 
                      display: 'block',
                      fontWeight: user.status === 'online' ? 600 : 400,
                      color: user.status === 'online' ? '#000' : '#8c8c8c'
                    }}
                    ellipsis
                  >
                    {user.display_name}
                  </Text>
                  
                  {isInternal && user.connections && user.connections > 1 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {user.connections} {t('members.connections')}
                    </Text>
                  ) || ''}
                  {!isInternal && user.power_level !== undefined && user.power_level > 0 && (
                    <Tag color="blue" style={{ fontSize: 10, marginTop: 2 }}>
                      {t('members.moderator')}
                    </Tag>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tooltip title={getStatusText(user.status)}>
                  {getStatusIcon(user.status)}
                </Tooltip>
                {user.status === 'offline' && user.lastSeen && (
                  <Tooltip title={`${t('members.lastActivity')}: ${formatLastSeen(user.lastSeen)}`}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {formatLastSeen(user.lastSeen)}
                    </Text>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </List.Item>
      );
    };

  const currentRoomData = getCurrentRoomData();
  const internalOnlineCount = currentRoomData?.internal_users.online.length || 0;
  const internalTotalCount = currentRoomData?.internal_users.total || 0;
  const matrixOnlineCount = currentRoomData?.matrix_users.online.length || 0;
  const matrixTotalCount = currentRoomData?.matrix_users.total || 0;

  return (
    <div className="members-sidebar">
      <div className="sidebar-header">
        <Title level={4} className="sidebar-title">
          {t('members.members')}
          <Text type="secondary" style={{ marginLeft: 8, fontSize: 14 }}>
            {(internalOnlineCount + matrixOnlineCount)}/{(internalTotalCount + matrixTotalCount)}
          </Text>
        </Title>
        <div style={{ display: 'flex', gap: 8 }}>
          {/*<Tooltip title="Обновить">
            <Button 
              icon={<ReloadOutlined />} 
              type="text" 
              size="small"
              loading={loading}
              onClick={loadOnlineMembers}
            />
          </Tooltip>*/}
          <Button 
            icon={<MenuFoldOutlined />} 
            type="text" 
            onClick={toggleMembersSidebar}
            className="sidebar-toggle"
          />
        </div>
      </div>

      <div className="members-sidebar-content" style={{ padding: '0 16px', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
        {loading && !currentRoomData ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin size="large" />
          </div>
        ) : !currentRoomData && currentRoomId ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Text type="secondary">{t('members.loadingRoomMembers', { roomId: currentRoomId })}</Text>
          </div>
        ) : !currentRoomId ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Text type="secondary">{t('members.selectRoomForMembers')}</Text>
          </div>
        ) : (
          <Collapse 
            ghost 
            activeKey={activePanels}
            style={{ background: 'transparent' }}
            onChange={handlePanelChange}
          >
            {/* Внутренние участники */}
            <Panel 
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TeamOutlined style={{ color: '#1890ff' }} />
                  <Text strong>{t('members.myTeam')}</Text>
                  <Badge 
                    count={internalOnlineCount} 
                    overflowCount={99}
                    style={{ backgroundColor: '#52c41a' }}
                  />
                </div>
              }
              key="internal"
            >
              <List
                size="small"
                dataSource={sortUsersByStatus([
                  ...(currentRoomData?.internal_users.online || []),
                  ...(currentRoomData?.internal_users.offline || [])
                ])}
                renderItem={(user) => renderUser(user, true)}
                locale={{ emptyText: t('members.noMembers') }}
              />
            </Panel>

            {/* Участники Matrix */}
            <Panel 
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CompassOutlined style={{ color: '#f56a00' }} />
                  <Text strong>{t('members.support')}</Text>
                  <Badge 
                    count={matrixOnlineCount} 
                    overflowCount={99}
                    style={{ backgroundColor: '#52c41a' }}
                  />
                </div>
              }
              key="matrix"
            >
              <List
                size="small"
                dataSource={sortUsersByStatus([
                  ...(currentRoomData?.matrix_users.online || []),
                  ...(currentRoomData?.matrix_users.offline || []),
                  ...(currentRoomData?.matrix_users.unavailable || []),
                  ...(currentRoomData?.matrix_users.unknown || [])
                ])}
                renderItem={(user) => renderUser(user, false)}
                locale={{ emptyText: t('members.noMembers') }}
              />
            </Panel>
          </Collapse>
        )}

        {lastUpdated && (
          <Text 
            type="secondary" 
            style={{ 
              fontSize: 11, 
              textAlign: 'center', 
              display: 'block', 
              marginTop: 16,
              padding: '8px 0'
            }}
          >
            {t('members.updated')}: {lastUpdated.toLocaleTimeString()} ({t('members.updatedAuto')})
          </Text>
        )}
      </div>
    </div>
  );
};

export default MembersList; 